import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Container, Button, Col, Row } from 'react-bootstrap';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { getDealer } from 'dealers/DealersActions';
import UserActivities from 'users/history/UserActivities';
import { reverse } from 'named-urls';
import classNames from 'classnames';
import validationSchema from './EditUserForm.schema';
import UserForm from '../form/UserForm';
import { fetchUser, updateUser, deleteUser } from '../UsersActions';
import CommonLoader from '../../common/components/CommonLoader';
import PageBreadcrumb from '../../common/components/layout/PageBreadcrumb';
import { userInitialValues, prepareUpdateData } from '../UsersUtils';
import ChangePasswordForm from '../form/ChangePasswordForm';
import RouterPaths from '../../constants/RouterPaths';
import NotificationSettingForm from '../form/NotificationSettingForm';

function EditUserPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [showAlert, setShowAlert] = useState(false);
  const [showOverridePassword, setShowOverridePassword] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);

  const dispatch = useDispatch();
  const loaderStatus = useSelector((state) => state.users.get('loaderStatus'));
  const userDetails = useSelector((state) => state.users.get('userDetails'));
  const organizationId = useSelector(
    (state) => state.users.get('userDetails')?.user?.organization?.id,
  );
  const organization = useSelector((state) => state.dealers.get('dealerDetails'));
  useEffect(() => {
    dispatch(fetchUser(id));
  }, [id, dispatch]);

  const mainProfile = organization?.mainProfile || {};
  const marketingPrefs = {
    phoneMarketingAllowed: !!mainProfile?.phoneMarketingAllowed,
    emailMarketingAllowed: !!mainProfile?.emailMarketingAllowed,
    smsMarketingAllowed: !!mainProfile?.smsMarketingAllowed,
    postMarketingAllowed: !!mainProfile?.postMarketingAllowed,
  };
  const initialValues = {
    ...userInitialValues(userDetails),
    ...marketingPrefs,
  };

  useEffect(() => {
    if (organizationId) {
      dispatch(getDealer(organizationId));
    }
  }, [organizationId, dispatch]);

  const handleSubmit = (values, { setSubmitting }) => {
    dispatch(
      updateUser(
        userDetails?.user?.id,
        prepareUpdateData({
          ...values,
          id: userDetails?.user?.id,
          version: userDetails?.user?.version,
        }),
        () => {
          setSubmitting(false);
        },
      ),
    );
  };
  const handleRemoveUser = () => {
    dispatch(deleteUser(userDetails?.user?.id));
  };

  const breadcrumbs = [
    { id: 1, type: 'home' },
    { id: 2, type: 'link', text: 'Users', url: '/users' },
    { id: 3, type: 'span', text: 'Edit User' },
  ];

  return (
    <Container fluid>
      <PageBreadcrumb items={breadcrumbs} pageTitle="Edit User" />
      <Row className="delete-button-wrapper">
        <Col xs={12} sm="12">
          <div className="edit-user__buttons-wrapper mb-2">
            <Button
              className={classNames('float-right rounded-pill btn-soft-dark', 'd-none')}
              onClick={() => setShowNotificationSettings(true)}
            >
              Notifications
            </Button>
            <Button
              className="float-right rounded-pill btn-soft-warning"
              onClick={() => setShowOverridePassword(true)}
            >
              Override Password
            </Button>
            <Button
              className="float-right rounded-pill btn-soft-danger"
              onClick={() => setShowAlert(true)}
            >
              Delete User
            </Button>
            <Button
              className="float-right rounded-pill btn-soft-success"
              onClick={() =>
                navigate(reverse(RouterPaths.DEALER_INFORMATION, { id: organizationId }))
              }
            >
              Go To Dealer
            </Button>
          </div>
        </Col>
      </Row>
      <CommonLoader status={loaderStatus}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur
        >
          {({ values }) => (
            <UserForm
              locations={userDetails?.organizationLocations}
              organizationRole={userDetails?.organizationRole}
              isOwner={userDetails?.user?.isOwner}
              values={values}
              organizationStatus={
                organizationId === organization?.organization?.id
                  ? organization?.mainProfile?.status
                  : ''
              }
            />
          )}
        </Formik>
      </CommonLoader>

      <UserActivities id={id} />

      <SweetAlert
        danger
        showCancel={false}
        showConfirm={false}
        show={showAlert}
        title="Warning!"
        onConfirm={() => {
          setShowAlert(false);
          handleRemoveUser();
        }}
        onCancel={() => setShowAlert(false)}
      >
        {(renderProps) => (
          <div>
            <p>Are you sure to remove a user?</p>
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button
                className="rounded-pill btn-soft-primary"
                onClick={() => renderProps.cancel()}
              >
                Cancel
              </Button>
              <Button
                className="rounded-pill btn-primary"
                onClick={() => renderProps?.confirm()}
              >
                Yes, Delete
              </Button>
            </div>
          </div>
        )}
      </SweetAlert>
      <SweetAlert
        onConfirm={() => {}}
        title={false}
        showConfirm={false}
        onCancel={() => setShowOverridePassword(false)}
        show={showOverridePassword}
        style={{ display: 'block', marginTop: '-100px' }}
      >
        <ChangePasswordForm onCloseAlert={() => setShowOverridePassword(false)} userId={id} />
      </SweetAlert>
      <SweetAlert
        onConfirm={() => {}}
        btnSize="lg"
        title={false}
        style={{ width: '100%', maxWidth: 550, paddingLeft: 32, paddingRight: 32 }}
        showConfirm={false}
        onCancel={() => setShowNotificationSettings(false)}
        show={showNotificationSettings}
      >
        <NotificationSettingForm
          onCloseAlert={() => setShowNotificationSettings(false)}
          userId={id}
        />
      </SweetAlert>
    </Container>
  );
}

EditUserPage.propTypes = {};

export default EditUserPage;
